/** @format */

import {
  Button,
  Skeleton,
  Group,
  Text,
  Title,
  Alert,
  Paper,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  templatesDetailsApi,
  templatesUpdateEditorContent,
} from "../../utilities/apis/templates";
import colors from "../../utilities/design";
import { loadingStates } from "../../utilities/utilities";
import Editor from "../Editor";
import DocxToHtmlUploader from "./UploadDocx";
import { getDocsToken } from "../../utilities/apis/agreements";
import constants from "../../utilities/constants";

function TemplateEditor() {
  const [placeholders, setPlaceholders] = useState();
  const editorRef = useRef();
  const [sfdtContent, setSfdtContent] = useState(null);
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [templatesConfig, setTemplatesConfig] = useState({
    loading: loadingStates.LOADING,
    editorContent: null,
    templateTitle: "",
    id: templateId,
  });

  const [uiConfigs, setUiConfigs] = useState({
    loading: loadingStates.NO_ACTIVE_REQUEST,
  });

  const getTemplate = async (tempId) => {
    // setTemplatesConfig((prevState) => ({
    //   ...prevState,
    //   loading: loadingStates.LOADING,
    // }));
    const response = await apiWrapWithErrorWithData(
      templatesDetailsApi({ templateId: tempId }),
    );
    if (response?.success && response?.template) {
      console.log(response);
      const accessToken = await apiWrapWithErrorWithData(getDocsToken());

      const response2 = await fetch(
        `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${response.template.quillDelta}/content`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );

      if (!response2.ok) {
        throw new Error("Failed to download template from OneDrive.");
      }

      const blob = await response2.blob();

      if (blob.size === 0) {
        console.warn(
          "Empty OneDrive document. Starting with a blank document.",
        );

        setTemplatesConfig((prev) => ({
          ...prev,
          loading: loadingStates.NO_ACTIVE_REQUEST,
          editorContent: response.template.quillDelta,
          templateTitle: response.template.name,
        }));

        setPlaceholders(response.template.fieldName || []);

        requestAnimationFrame(() => {
          editorRef.current?.createBlankDocument();
        });

        return;
      }

      const file = new File([blob], "template.docx", {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append("files", file);
      try {
        const sfdtResponse = await fetch(
          `${constants.wordeditorApi}/api/documenteditor/Import`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!sfdtResponse.ok) {
          const err = await sfdtResponse.text();
          throw new Error(`Import API failed (${sfdtResponse.status}): ${err}`);
        }

        // Import API returns SFDT as text
        const sfdt = await sfdtResponse.text();

        if (!sfdt || !sfdt.trim()) {
          throw new Error("Import API returned an empty response.");
        }

        setSfdtContent(sfdt);
      } catch (error) {
        console.error("Error importing document:", error);
        setTemplatesConfig({
          ...templatesConfig,
          loading: loadingStates.NO_ACTIVE_REQUEST,
          editorContent: response.template.quillDelta,
          templateTitle: response.template.name,
        });
      }

      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
        editorContent: response.template.quillDelta,
        templateTitle: response.template.name,
      });
      setPlaceholders(response.template.fieldName || []);
    } else {
      // showNotification({
      //   color: "red",
      //   title: "Templates Details",
      //   message: "Failed to load template details.",
      // });
      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };
  useEffect(() => {
    getTemplate(templateId);
  }, []);

  const saveTemplate = async (tempId) => {
    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });
    const response = await apiWrapWithErrorWithData(
      templatesUpdateEditorContent({
        templateId: tempId,
        quillDelta: templatesConfig.editorContent,
        templateTitle: templatesConfig.templateTitle,
        fieldName: placeholders,
      }),
    );
    if (response?.success) {
      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
        editorContent: response.quillContent.quillDelta,
      });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    } else {
      showNotification({
        color: "red",
        title: "Templates Details",
        message: "Failed to load template details.",
      });
    }
  };

  const saveContent = async () => {
    if (!editorRef.current) return;

    try {
      const updatedSfdt = editorRef.current.getSfdt();

      if (!updatedSfdt) {
        showNotification({
          color: "red",
          title: "Save Failed",
          message: "Document is empty.",
        });
        return;
      }

      // Export DOCX using Editor.jsx
      const blob = await editorRef.current.saveDocument();

      if (!blob) {
        throw new Error("Unable to export document.");
      }

      console.log("Uploading template:", templatesConfig.editorContent);

      const accessToken = await apiWrapWithErrorWithData(getDocsToken());

      const uploadResponse = await fetch(
        `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${templatesConfig.editorContent}/content`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
          body: blob,
        },
      );

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload DOCX to OneDrive.");
      }

      await saveTemplate(templateId);

      showNotification({
        color: "green",
        title: "Template Saved",
        message: "Template has been saved successfully.",
      });

      navigate("/app/agreements_manager/templates/agreement");
    } catch (error) {
      console.error("Save Error:", error);

      showNotification({
        color: "red",
        title: "Save Failed",
        message: error.message || "Failed to save template.",
      });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen p-8">
      <div className="max-w-[1700px] mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div>
            <Button
              variant="subtle"
              onClick={() => navigate(-1)}
              className="mb-3"
            >
              ← Back to Templates
            </Button>

            <Title order={2}>Template Editor</Title>
            <Text
              c="dimmed"
              mt={4}
              style={{ textTransform: "none", color: "GrayText" }}
            >
              Design reusable contract templates with dynamic placeholders.
            </Text>
          </div>

          <Group>
            <Button variant="default" radius="md" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              radius="md"
              size="md"
              loading={uiConfigs.loading === loadingStates.LOADING}
              onClick={saveContent}
            >
              Save Template
            </Button>
          </Group>
        </div>
        {templatesConfig.loading === loadingStates.LOADING && (
          <div className="flex flex-col">
            <Skeleton height={40} className="mb-11" />
            <Skeleton height={40} className="mb-11" />
            <Skeleton height={40} className="mb-11" />
            <Skeleton height={40} className="mb-11" />
            <Skeleton height={40} className="mb-11" />
          </div>
        )}
        {uiConfigs.loading === loadingStates.LOADING ||
        templatesConfig.loading === loadingStates.LOADING ? (
          <BeatLoader color={colors.primary} size={10} />
        ) : (
          <></>
        )}
        {templatesConfig.loading !== loadingStates.LOADING && (
          <div className="flex flex-col">
            <Alert color="blue" variant="light" mb="lg">
              Changes are stored only after clicking
              <b> Save Template</b>.
            </Alert>

            <Paper shadow="sm" radius="lg" withBorder p="xl" mb="lg">
              <Title order={5} mb="md">
                Template Details
              </Title>

              <TextInput
                radius="md"
                label="Template Title"
                placeholder="Enter template name"
                value={templatesConfig.templateTitle}
                onChange={(e) =>
                  setTemplatesConfig({
                    ...templatesConfig,
                    templateTitle: e.target.value,
                  })
                }
              />
            </Paper>
            {/* <div>
            <DocxToHtmlUploader
              htmlContent={templatesConfig.editorContent.content}
              setHtmlContent={setContent}
            />
          </div> */}
            <Paper radius="lg" withBorder shadow="sm" p="md">
              <Editor
                placeholders={placeholders}
                setPlaceholders={setPlaceholders}
                ref={editorRef}
                initialContent={sfdtContent}
              />
            </Paper>
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateEditor;
